/**
 * useReservationChat — 自社チャット（予約者チャット）を LINE ページから読む・返信する
 *
 * 設計: booking-platform/md/customer-chat.md（Phase 2）／ md/master_md/page-line-chat.md「自社チャットの統合」
 *
 * - API は new-n-car の `chat.php`（店舗別。instances.ts `chatApiUrl`）。LINE API（sys/line）とは別物。
 * - 認証はスタッフの Bearer（この店舗の backend の Sanctum トークン＝r-cal のログイン）。chat.php が
 *   backend /auth/me で検証する（`get_sessions` / `sender=admin` の送信 / `role=admin` の既読は Bearer 必須）。
 *   chat.php は `/sys/` 配下ではないので internalApiAuth の fetch ラッパーは効かない → ここで明示的に付ける。
 * - リアルタイム（2026-10-11）: `services/reservationChatPusher.ts` が LP の Pusher アプリに 2 本目の接続を張り、
 *   `private-admin-chat` のイベントを `applyReservationChatRealtime` でここの共有一覧に反映する。ポーリングは保険として残す
 *   （接続中は一覧 60 秒・会話 60 秒、未接続は 30 秒・5 秒）。接続状態は `realtimeConnected`。
 * - 送信時翻訳（`translateTo`）: LP の `translate`（Bearer）で日本語→お客様の言語にしてから `send_message` に
 *   `original_text / original_lang / translated_lang` を付けて送る（LP 管理画面の自動翻訳 ON と同じ契約）。翻訳に失敗したら送らない。
 * - 画像（`uploadReservationChatImage`）: LP の `upload_image`（multipart・Bearer）。URL は署名付き・期限付き。
 * - 2026-10-26（/admin の統合・booking-platform/md/customer-chat.md §13）: 一覧は `get_sessions&scope=board`
 *   （予約チャット全件＋匿名は直近 30 日・ピン留め）。匿名チャットもここで扱い、LP 管理画面にしか無かった操作
 *   （対応中・メモ・IP/国ブロック・チャット受付 ON/OFF）を足した。デスクトップ通知は services/chatPush.ts。
 * - 失敗は必ず logEvent（無言の失敗を作らない）。
 */
import { useState, useCallback, useEffect } from 'react';
import { getReservationChatApiUrl } from '../config/backend';
import { getAuthToken } from '../api/dispatchClient';
import { logEvent } from '../lib/eventLog';
import {
  chatAuthHeaders,
  type ReservationChatSession,
  type ReservationChatMessage,
} from '../lib/reservationChat';
import { applyRealtimeEvent } from '../lib/reservationChatRealtime';
import { getReservationChatStoreKey } from '../config/backend';
import { ANON_BOARD_DAYS } from '../lib/lpChatBoard';
import { playNotificationSound } from '../lib/soundUtils';

export interface ReservationChatSuggestion {
  id: number;
  reply: string;
  action_type: string;
  confidence: number;
  alert_level: number;
  reasoning: string;
  created_at: string;
  cached?: boolean;
}

// ---- 共有状態（LineChatBoard と ReservationChatPanel が同じ一覧を見る） ----
let sharedSessions: ReservationChatSession[] = [];
let sharedLoading = false;
let sharedLastError: string | null = null;
/** 一覧の取得が 1 回でも終わったか（成功・失敗とも）。/line の URL で会話を開く処理が「見つからない」を判断するのに使う */
let sharedLoaded = false;
let listeners: Array<() => void> = [];
let lastRequestId = 0;
/** LP の Pusher（private-admin-chat）を購読できているか。ポーリング間隔の切替に使う */
let sharedRealtimeConnected = false;
/** 開いている会話パネルへ新着メッセージを渡す（session_key → コールバック） */
type RealtimeMessage = ReservationChatMessage & { session_key: string };
let messageListeners: Array<{ sessionKey: string; cb: (m: RealtimeMessage) => void }> = [];
let refetchTimer: ReturnType<typeof setTimeout> | null = null;
/** 今開いている自社チャットの会話（その会話の新着では受信音を鳴らさない。LINE の sharedOpenLineUserId と同じ考え方） */
let openSessionKey: string | null = null;
export const setOpenReservationChatSession = (sessionKey: string | null): void => {
  openSessionKey = sessionKey;
};

const notify = () => listeners.forEach((l) => l());

export const setReservationChatRealtimeConnected = (connected: boolean): void => {
  if (sharedRealtimeConnected === connected) return;
  sharedRealtimeConnected = connected;
  notify();
};
export const isReservationChatRealtimeConnected = (): boolean => sharedRealtimeConnected;

/** 会話パネル用: この会話の新着（Pusher 経由）を受け取る。戻り値で解除 */
export const subscribeReservationChatMessages = (sessionKey: string, cb: (m: RealtimeMessage) => void): (() => void) => {
  const entry = { sessionKey, cb };
  messageListeners.push(entry);
  return () => {
    messageListeners = messageListeners.filter((x) => x !== entry);
  };
};

/**
 * Pusher のイベントを共有一覧に反映する（services/reservationChatPusher.ts から呼ばれる）。
 * 他店舗のイベントは無視。一覧に無い会話（新しい会話）は 1 秒まとめて取り直す。
 */
export const applyReservationChatRealtime = (eventName: string, data: any): void => {
  const r = applyRealtimeEvent(sharedSessions, eventName, data, getReservationChatStoreKey());
  if (r.sessions !== sharedSessions) {
    sharedSessions = r.sessions;
    notify();
  }
  if (r.refetch && !refetchTimer) {
    refetchTimer = setTimeout(() => {
      refetchTimer = null;
      fetchReservationChatSessions();
    }, 1000);
  }
  // 受信音（お客様の新着だけ。silent＝プリセット・ジャンク等は applyRealtimeEvent が customerNew=false にする）。
  // 一覧に無い新しい会話（refetch）でも鳴らす。開いている会話は鳴らさない
  if (r.customerNew && r.message && r.message.session_key !== openSessionKey) {
    playNotificationSound();
  }
  if (r.message) {
    const m = r.message;
    messageListeners.forEach((l) => {
      if (l.sessionKey !== m.session_key) return;
      try {
        l.cb(m);
      } catch (e) {
        logEvent({ action: 'resv_chat.realtime.listener', target: m.session_key, result: 'fail', err: e });
      }
    });
  }
};

const apiUrl = () => getReservationChatApiUrl();

const readToken = (): string | null => {
  const t = getAuthToken();
  if (t) return t;
  try {
    return window.localStorage.getItem('authToken');
  } catch {
    return null;
  }
};

/** 401 は「ログイン切れ」としてメッセージを分ける（LP 側は {error:'Unauthorized', code:'auth_expired'}） */
const describeHttp = async (res: Response): Promise<string> => {
  let body = '';
  try {
    body = (await res.text()).slice(0, 300);
  } catch {
    /* ignore */
  }
  return `HTTP ${res.status} ${body}`;
};

/** 一覧を取り直す。無効店舗（URL 空）は何もしない */
export async function fetchReservationChatSessions(): Promise<ReservationChatSession[] | null> {
  const url = apiUrl();
  if (!url) return [];
  const requestId = ++lastRequestId;
  sharedLoading = true;
  notify();
  const started = Date.now();
  try {
    const res = await fetch(`${url}?action=get_sessions&scope=board&days=${ANON_BOARD_DAYS}`, { headers: chatAuthHeaders(readToken()) });
    if (!res.ok) throw new Error(await describeHttp(res));
    const data = await res.json();
    if (!data || !Array.isArray(data.sessions)) throw new Error(`unexpected response: ${JSON.stringify(data).slice(0, 200)}`);
    if (requestId !== lastRequestId) return null; // 古い応答は捨てる
    sharedSessions = data.sessions as ReservationChatSession[];
    sharedLastError = null;
    return sharedSessions;
  } catch (e) {
    if (requestId === lastRequestId) sharedLastError = e instanceof Error ? e.message : String(e);
    logEvent({ action: 'resv_chat.fetch_sessions', result: 'fail', ms: Date.now() - started, err: e });
    return null;
  } finally {
    if (requestId === lastRequestId) {
      sharedLoading = false;
      sharedLoaded = true;
      notify();
    }
  }
}

/** 会話のメッセージ（lastId より新しいものだけ。0 なら全件） */
export async function fetchReservationChatMessages(
  sessionKey: string,
  lastId = 0,
): Promise<{ messages: ReservationChatMessage[]; session_status: string; admin_handling: boolean } | null> {
  const url = apiUrl();
  if (!url) return null;
  const started = Date.now();
  try {
    const q = `${url}?action=get_messages&session_key=${encodeURIComponent(sessionKey)}&last_id=${lastId}`;
    const res = await fetch(q, { headers: chatAuthHeaders(readToken()) });
    if (!res.ok) throw new Error(await describeHttp(res));
    const data = await res.json();
    if (!data || !Array.isArray(data.messages)) throw new Error(`unexpected response: ${JSON.stringify(data).slice(0, 200)}`);
    return {
      messages: data.messages.map((m: any) => ({ ...m, id: Number(m.id) })),
      session_status: data.session_status || 'ACTIVE',
      admin_handling: Boolean(data.admin_handling),
    };
  } catch (e) {
    logEvent({ action: 'resv_chat.fetch_messages', target: sessionKey, result: 'fail', ms: Date.now() - started, err: e });
    return null;
  }
}

/**
 * LP の `translate`（Bearer 必須）で翻訳する。LINE の翻訳 API ではなく LP 側（Gemini）を使うので、
 * 保存される言語コードが LP 管理画面と揃う。失敗は null（呼び出し側は送らない）。
 */
export async function translateReservationChatText(
  text: string,
  targetLang: string,
): Promise<{ translated_text: string; source_lang: string } | null> {
  const url = apiUrl();
  if (!url) return null;
  const started = Date.now();
  try {
    const res = await fetch(`${url}?action=translate`, {
      method: 'POST',
      headers: chatAuthHeaders(readToken()),
      body: JSON.stringify({ text, target_lang: targetLang }),
    });
    if (!res.ok) throw new Error(await describeHttp(res));
    const data = await res.json();
    if (!data?.success || typeof data.translated_text !== 'string' || !data.translated_text.trim()) {
      throw new Error(`translate rejected: ${JSON.stringify(data).slice(0, 200)}`);
    }
    return { translated_text: data.translated_text, source_lang: String(data.source_lang || 'ja') };
  } catch (e) {
    logEvent({ action: 'resv_chat.translate', target: targetLang, result: 'fail', ms: Date.now() - started, err: e });
    return null;
  }
}

export interface SendReservationChatOptions {
  /** 指定すると日本語の入力をこの言語に翻訳して送る（原文は original_text として保存）。'ja'・空は翻訳なし */
  translateTo?: string | null;
}

/**
 * スタッフ名義で送る（sender=admin・Bearer 必須）。
 * 200 でも `success:false` が返り得る（LP の仕様）ので本文まで見て成否を決める。
 * `translateTo` があれば先に LP の translate で翻訳し、**翻訳できなければ送らない**（日本語のまま黙って送らない）。
 */
export async function sendReservationChatMessage(
  sessionKey: string,
  text: string,
  options: SendReservationChatOptions = {},
): Promise<{ success: boolean; id?: number; error?: string; sent_text?: string; original_text?: string | null; translated_lang?: string | null }> {
  const url = apiUrl();
  if (!url) return { success: false, error: 'この店舗では自社チャットが無効です' };
  const target = options.translateTo && options.translateTo !== 'ja' ? options.translateTo : null;
  const started = Date.now();
  let body: Record<string, unknown> = { session_key: sessionKey, sender: 'admin', text };
  let sentText = text;
  if (target) {
    const tr = await translateReservationChatText(text, target);
    if (!tr) {
      logEvent({ action: 'resv_chat.send', target: sessionKey, result: 'fail', ms: Date.now() - started, err: `translate failed (${target})` });
      return { success: false, error: `翻訳に失敗したため送信していません（→${target}）。再試行するか、自動翻訳を OFF にして送ってください` };
    }
    sentText = tr.translated_text;
    body = { ...body, text: sentText, original_text: text, original_lang: 'ja', translated_lang: target };
  }
  try {
    const res = await fetch(`${url}?action=send_message`, {
      method: 'POST',
      headers: chatAuthHeaders(readToken()),
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(await describeHttp(res));
    const data = await res.json();
    if (!data?.success || !data.id) throw new Error(`send rejected: ${JSON.stringify(data).slice(0, 200)}`);
    logEvent({
      action: 'resv_chat.send', target: sessionKey, result: 'ok', ms: Date.now() - started,
      detail: { id: data.id, len: text.length, translated_to: target },
    });
    return { success: true, id: Number(data.id), sent_text: sentText, original_text: target ? text : null, translated_lang: target };
  } catch (e) {
    logEvent({ action: 'resv_chat.send', target: sessionKey, result: 'fail', ms: Date.now() - started, err: e });
    return { success: false, error: e instanceof Error ? e.message : String(e) };
  }
}

/**
 * 画像を 1 枚送る（LP `upload_image`・multipart・Bearer 必須）。LP が再エンコードして保存し `[画像]` のメッセージを作る。
 * 成功時は LP が返したメッセージ（attachment の署名付き URL 入り）をそのまま返す。
 */
export async function uploadReservationChatImage(
  sessionKey: string,
  file: File,
): Promise<{ success: true; message: ReservationChatMessage } | { success: false; error: string }> {
  const url = apiUrl();
  if (!url) return { success: false, error: 'この店舗では自社チャットが無効です' };
  const started = Date.now();
  try {
    const form = new FormData();
    form.append('session_key', sessionKey);
    form.append('file', file, file.name || 'image');
    const headers = chatAuthHeaders(readToken());
    delete headers['Content-Type']; // multipart の boundary はブラウザに任せる
    const res = await fetch(`${url}?action=upload_image`, { method: 'POST', headers, body: form });
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      const code = data?.error ? String(data.error) : '';
      const human = code === 'too_large' ? '画像が大きすぎます（10MB まで）'
        : code === 'unsupported_type' || code === 'heic_unsupported' || code === 'image_decode_failed' ? 'この形式の画像は送れません（JPEG / PNG）'
          : `HTTP ${res.status} ${code}`.trim();
      throw new Error(human);
    }
    if (!data?.success || !data.message) throw new Error(`upload rejected: ${JSON.stringify(data).slice(0, 200)}`);
    const message = { ...data.message, id: Number(data.message.id) } as ReservationChatMessage;
    logEvent({ action: 'resv_chat.upload_image', target: sessionKey, result: 'ok', ms: Date.now() - started, detail: { id: message.id, bytes: file.size, mime: file.type } });
    return { success: true, message };
  } catch (e) {
    logEvent({ action: 'resv_chat.upload_image', target: sessionKey, result: 'fail', ms: Date.now() - started, err: e });
    return { success: false, error: e instanceof Error ? e.message : String(e) };
  }
}

/** スタッフ既読（role=admin・Bearer 必須）。成功したら共有一覧の既読位置も進める */
export async function markReservationChatRead(sessionKey: string, lastReadId?: number): Promise<boolean> {
  const url = apiUrl();
  if (!url) return false;
  const started = Date.now();
  try {
    const body: Record<string, unknown> = { session_key: sessionKey, role: 'admin' };
    if (lastReadId && lastReadId > 0) body.last_read_id = lastReadId;
    const res = await fetch(`${url}?action=mark_read`, {
      method: 'POST',
      headers: chatAuthHeaders(readToken()),
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(await describeHttp(res));
    const data = await res.json();
    if (!data?.success) throw new Error(`mark_read rejected: ${JSON.stringify(data).slice(0, 200)}`);
    const confirmed = Number(data.last_read_id || lastReadId || 0);
    sharedSessions = sharedSessions.map((s) =>
      s.session_key === sessionKey
        ? { ...s, admin_last_read_id: Math.max(Number(s.admin_last_read_id || 0), confirmed), admin_last_read_at: data.last_read_at || s.admin_last_read_at }
        : s,
    );
    notify();
    return true;
  } catch (e) {
    logEvent({ action: 'resv_chat.mark_read', target: sessionKey, result: 'fail', ms: Date.now() - started, err: e });
    return false;
  }
}

/** ピン留め（update_session_meta・Bearer 必須）。楽観更新し、失敗したら戻す */
export async function toggleReservationChatPin(sessionKey: string, isPinned: boolean): Promise<boolean> {
  const url = apiUrl();
  if (!url) return false;
  const prev = sharedSessions;
  sharedSessions = sharedSessions.map((s) => (s.session_key === sessionKey ? { ...s, is_pinned: isPinned ? 1 : 0 } : s));
  notify();
  const started = Date.now();
  try {
    const res = await fetch(`${url}?action=update_session_meta`, {
      method: 'POST',
      headers: chatAuthHeaders(readToken()),
      body: JSON.stringify({ session_key: sessionKey, is_pinned: isPinned }),
    });
    if (!res.ok) throw new Error(await describeHttp(res));
    const data = await res.json();
    if (!data?.success) throw new Error(`update_session_meta rejected: ${JSON.stringify(data).slice(0, 200)}`);
    return true;
  } catch (e) {
    sharedSessions = prev;
    notify();
    logEvent({ action: 'resv_chat.toggle_pin', target: sessionKey, result: 'fail', ms: Date.now() - started, err: e });
    return false;
  }
}

/** アーカイブ／解除（Bearer 必須）。LP 側は顧客にも「担当者がチャットを終了しました。」を出す */
export async function setReservationChatArchived(sessionKey: string, archived: boolean): Promise<boolean> {
  const url = apiUrl();
  if (!url) return false;
  const started = Date.now();
  const action = archived ? 'archive_session' : 'unarchive_session';
  try {
    const res = await fetch(`${url}?action=${action}`, {
      method: 'POST',
      headers: chatAuthHeaders(readToken()),
      body: JSON.stringify({ session_key: sessionKey }),
    });
    if (!res.ok) throw new Error(await describeHttp(res));
    const data = await res.json();
    if (!data?.success) throw new Error(`${action} rejected: ${JSON.stringify(data).slice(0, 200)}`);
    sharedSessions = sharedSessions.map((s) => (s.session_key === sessionKey ? { ...s, status: archived ? 'ARCHIVED' : 'ACTIVE' } : s));
    notify();
    logEvent({ action: `resv_chat.${action}`, target: sessionKey, result: 'ok', ms: Date.now() - started });
    return true;
  } catch (e) {
    logEvent({ action: `resv_chat.${action}`, target: sessionKey, result: 'fail', ms: Date.now() - started, err: e });
    return false;
  }
}

/**
 * AI 返信案（suggest_reply・Bearer 必須）。LP 側が直近 5 分の ai_shadow_replies を返すか、
 * 無ければ最新の顧客メッセージから同期生成する。無効（AI_SUGGEST_ENABLED=false）なら null。
 */
export async function suggestReservationChatReply(sessionKey: string): Promise<ReservationChatSuggestion | null> {
  const url = apiUrl();
  if (!url) return null;
  const started = Date.now();
  try {
    const res = await fetch(`${url}?action=suggest_reply&session_key=${encodeURIComponent(sessionKey)}`, {
      headers: chatAuthHeaders(readToken()),
    });
    if (!res.ok) throw new Error(await describeHttp(res));
    const data = await res.json();
    if (!data?.success || !data.suggestion) throw new Error(data?.error || `unexpected response: ${JSON.stringify(data).slice(0, 200)}`);
    return { ...data.suggestion, cached: Boolean(data.cached) } as ReservationChatSuggestion;
  } catch (e) {
    logEvent({ action: 'resv_chat.suggest_reply', target: sessionKey, result: 'fail', ms: Date.now() - started, err: e });
    return null;
  }
}

export interface ReservationChatInvite {
  url: string;
  contact_code: string;
  /** UNIX 秒（LP 側 `expires_at`） */
  expires_at: number;
}

/**
 * 招待リンクを発行する（resv_invite・Bearer 必須・LP 側 $ADMIN_ACTIONS）。
 * 電話番号もメールも無い予約（TR- など）向け。リンクは **1回限り**・期限は 72 時間と返却日+7日の早い方。
 * 発行は LP 側で `chat_resv_invites` と error_log に残る（発行者=スタッフ ID）。
 * ここでは LP の試行回数（IP 単位）に数えない（`chat_resv_load_reservation(..., false)`）。
 */
export async function issueReservationChatInvite(
  contactCode: string,
): Promise<{ success: true; invite: ReservationChatInvite } | { success: false; error: string }> {
  const url = apiUrl();
  if (!url) return { success: false, error: 'この店舗では自社チャットが無効です' };
  const code = String(contactCode || '').trim();
  if (!code) return { success: false, error: '連絡コードが分かりません' };
  const started = Date.now();
  try {
    const res = await fetch(`${url}?action=resv_invite`, {
      method: 'POST',
      headers: chatAuthHeaders(readToken()),
      body: JSON.stringify({ contact_code: code }),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error(`HTTP ${res.status} ${data?.error || ''}`.trim());
    if (!data?.success || typeof data.url !== 'string' || !data.url) {
      throw new Error(`resv_invite rejected: ${JSON.stringify(data).slice(0, 200)}`);
    }
    const invite: ReservationChatInvite = {
      url: data.url,
      contact_code: String(data.contact_code || code),
      expires_at: Number(data.expires_at || 0),
    };
    // detail に URL（トークン）は書かない。期限だけ残す
    logEvent({ action: 'resv_chat.invite', target: code, result: 'ok', ms: Date.now() - started, detail: { expires_at: invite.expires_at } });
    return { success: true, invite };
  } catch (e) {
    logEvent({ action: 'resv_chat.invite', target: code, result: 'fail', ms: Date.now() - started, err: e });
    return { success: false, error: e instanceof Error ? e.message : String(e) };
  }
}

/**
 * 下4桁の失敗ロック（5回で 30 分）を解除する（resv_unlock・Bearer 必須）。
 * LP 側は `chat_resv_guard` の fail_count / locked_until をリセットし、解除者のスタッフ ID をログに残す。
 * ロック中かどうかはスタッフ向け API が無い（`resv_start` は公開 API で IP 単位の試行回数に数えるため呼ばない）。
 * 引数は **予約 ID**（連絡コードではない）。
 */
export async function unlockReservationChat(reservationId: string | number): Promise<{ success: boolean; error?: string }> {
  const url = apiUrl();
  if (!url) return { success: false, error: 'この店舗では自社チャットが無効です' };
  const rid = String(reservationId ?? '').trim();
  if (!rid) return { success: false, error: '予約 ID が分かりません' };
  const started = Date.now();
  try {
    const res = await fetch(`${url}?action=resv_unlock`, {
      method: 'POST',
      headers: chatAuthHeaders(readToken()),
      body: JSON.stringify({ reservation_id: rid }),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error(`HTTP ${res.status} ${data?.error || ''}`.trim());
    if (!data?.success) throw new Error(`resv_unlock rejected: ${JSON.stringify(data).slice(0, 200)}`);
    logEvent({ action: 'resv_chat.unlock', target: rid, result: 'ok', ms: Date.now() - started });
    return { success: true };
  } catch (e) {
    logEvent({ action: 'resv_chat.unlock', target: rid, result: 'fail', ms: Date.now() - started, err: e });
    return { success: false, error: e instanceof Error ? e.message : String(e) };
  }
}

/** POST して `success` まで確かめる共通処理（LP 管理系 API・Bearer 必須）。失敗は logEvent して null */
async function postAdmin(action: string, body: Record<string, unknown>, logTarget: string): Promise<any | null> {
  const url = apiUrl();
  if (!url) return null;
  const started = Date.now();
  try {
    const res = await fetch(`${url}?action=${action}`, {
      method: 'POST',
      headers: chatAuthHeaders(readToken()),
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(await describeHttp(res));
    const data = await res.json();
    if (!data?.success) throw new Error(`${action} rejected: ${JSON.stringify(data).slice(0, 200)}`);
    logEvent({ action: `resv_chat.${action}`, target: logTarget, result: 'ok', ms: Date.now() - started });
    return data;
  } catch (e) {
    logEvent({ action: `resv_chat.${action}`, target: logTarget, result: 'fail', ms: Date.now() - started, err: e });
    return null;
  }
}

const patchSession = (sessionKey: string, patch: Partial<ReservationChatSession>) => {
  sharedSessions = sharedSessions.map((s) => (s.session_key === sessionKey ? { ...s, ...patch } : s));
  notify();
};

/** 対応中（set_admin_handling）。minutes=0 で解除。LP 管理画面の「対応中」と同じ（お客様側の AI 自動返信が止まる） */
export async function setReservationChatHandling(sessionKey: string, minutes: number): Promise<boolean> {
  const data = await postAdmin('set_admin_handling', { session_key: sessionKey, minutes }, sessionKey);
  if (!data) return false;
  patchSession(sessionKey, { admin_handling: Boolean(data.admin_handling), admin_handling_until: data.admin_handling_until ?? null });
  return true;
}

/** メモ（update_session_meta）。予約チャットの自動メモ「予約 {連絡コード} {氏名}」もスタッフが書き換えられる */
export async function updateReservationChatMemo(sessionKey: string, memo: string): Promise<boolean> {
  const data = await postAdmin('update_session_meta', { session_key: sessionKey, memo }, sessionKey);
  if (!data) return false;
  patchSession(sessionKey, { memo });
  return true;
}

/** IP ブロック／解除（匿名チャット）。成功したら同じ IP の会話にブロック印を付け直すため一覧を取り直す */
export async function setChatIpBlocked(ip: string, blocked: boolean, reason?: string): Promise<boolean> {
  const data = await postAdmin(blocked ? 'block_ip' : 'unblock_ip', blocked ? { ip, reason: reason || null, blocked_by: 'line_page' } : { ip }, ip);
  if (!data) return false;
  fetchReservationChatSessions();
  return true;
}

/** 国ブロック／解除（ISO 3166-1 alpha-2） */
export async function setChatCountryBlocked(countryCode: string, blocked: boolean, countryName?: string | null): Promise<boolean> {
  const data = await postAdmin(
    blocked ? 'block_country' : 'unblock_country',
    blocked ? { country_code: countryCode, country_name: countryName || null, blocked_by: 'line_page' } : { country_code: countryCode },
    countryCode,
  );
  if (!data) return false;
  fetchReservationChatSessions();
  return true;
}

/**
 * スタッフのアーカイブ（一覧から外す／戻す・staff_archive・Bearer 必須・§13-5）。お客様の画面は変わらない。
 * 楽観更新し、失敗したら戻す。LINE と予約チャットをまとめたカードは呼び出し側が両方を扱う
 */
export async function setLpChatStaffArchived(sessionKeys: string[], archived: boolean): Promise<boolean> {
  const keys = sessionKeys.filter(Boolean);
  if (keys.length === 0) return true;
  const prev = sharedSessions;
  const at = archived ? new Date().toISOString() : null;
  const set = new Set(keys);
  sharedSessions = sharedSessions.map((s) => (set.has(s.session_key) ? { ...s, staff_archived_at: at } : s));
  notify();
  const data = await postAdmin('staff_archive', { session_keys: keys, archived }, keys.join(','));
  if (!data) {
    sharedSessions = prev;
    notify();
    return false;
  }
  if (data.staff_archived_at !== undefined) {
    sharedSessions = sharedSessions.map((s) => (set.has(s.session_key) ? { ...s, staff_archived_at: data.staff_archived_at } : s));
    notify();
  }
  return true;
}

/** チャット受付の状態（get_status は公開 API）。取れなければ null */
export async function fetchChatAccepting(): Promise<boolean | null> {
  const url = apiUrl();
  if (!url) return null;
  try {
    const res = await fetch(`${url}?action=get_status&_t=${Date.now()}`);
    if (!res.ok) throw new Error(await describeHttp(res));
    const data = await res.json();
    if (typeof data?.isChatEnabled !== 'boolean') throw new Error(`unexpected response: ${JSON.stringify(data).slice(0, 200)}`);
    return data.isChatEnabled;
  } catch (e) {
    logEvent({ action: 'resv_chat.get_status', result: 'fail', err: e });
    return null;
  }
}

/** チャット受付 ON/OFF（update_status・Bearer 必須）。LP のウィジェットへは LP が Pusher で配る。cron の営業時間切替は別に動く */
export async function setChatAccepting(enabled: boolean): Promise<boolean | null> {
  const data = await postAdmin('update_status', { enabled }, enabled ? 'on' : 'off');
  return data ? Boolean(data.isChatEnabled) : null;
}

/** 共有一覧を購読する hook。fetch 系は上の関数をそのまま返す */
export const useReservationChat = () => {
  const [sessions, setSessions] = useState<ReservationChatSession[]>(sharedSessions);
  const [loading, setLoading] = useState<boolean>(sharedLoading);
  const [loaded, setLoaded] = useState<boolean>(sharedLoaded);
  const [lastError, setLastError] = useState<string | null>(sharedLastError);
  const [realtimeConnected, setRealtimeConnected] = useState<boolean>(sharedRealtimeConnected);

  useEffect(() => {
    const l = () => {
      setSessions(sharedSessions);
      setLoading(sharedLoading);
      setLoaded(sharedLoaded);
      setLastError(sharedLastError);
      setRealtimeConnected(sharedRealtimeConnected);
    };
    listeners.push(l);
    return () => {
      listeners = listeners.filter((x) => x !== l);
    };
  }, []);

  const fetchSessions = useCallback(fetchReservationChatSessions, []);

  return {
    enabled: apiUrl() !== '',
    sessions,
    loading,
    loaded,
    lastError,
    realtimeConnected,
    fetchSessions,
    fetchMessages: fetchReservationChatMessages,
    sendMessage: sendReservationChatMessage,
    translateText: translateReservationChatText,
    uploadImage: uploadReservationChatImage,
    subscribeMessages: subscribeReservationChatMessages,
    markRead: markReservationChatRead,
    togglePin: toggleReservationChatPin,
    setArchived: setReservationChatArchived,
    suggestReply: suggestReservationChatReply,
    issueInvite: issueReservationChatInvite,
    unlock: unlockReservationChat,
    setHandling: setReservationChatHandling,
    updateMemo: updateReservationChatMemo,
    setIpBlocked: setChatIpBlocked,
    setStaffArchived: setLpChatStaffArchived,
    setCountryBlocked: setChatCountryBlocked,
  };
};

/** テスト用: 共有状態を初期化 */
export const __resetReservationChatState = () => {
  sharedSessions = [];
  sharedLoading = false;
  sharedLoaded = false;
  sharedLastError = null;
  lastRequestId = 0;
  sharedRealtimeConnected = false;
  messageListeners = [];
  if (refetchTimer) {
    clearTimeout(refetchTimer);
    refetchTimer = null;
  }
};
/** テスト用: 共有一覧を直接入れる */
export const __setReservationChatSessions = (sessions: ReservationChatSession[]) => {
  sharedSessions = sessions;
  sharedLoaded = true;
  notify();
};
export const __getReservationChatSessions = () => sharedSessions;
